/**
 * The Terms of Use, as structured text the /terms page and the acceptance dialog render.
 *
 * The text itself is public/legal/terms.json, fetched when somebody opens it: about 26 KB that
 * nobody else should download with the app (the JavaScript has a size budget in CI). It is kept out
 * of the locale files on purpose: a legal document with its own version, read as a whole, and a
 * translator should be handed it as one piece. The same text is in docs/legal/termos-de-uso.md for
 * the owner and their lawyer; change both together, and when a change needs people to agree again,
 * move TERMS_VERSION here, the dates in terms.json and TermsOfUse.CurrentVersion in the API
 * (backend/src/DevPulse.Domain/Identity/TermsOfUse.cs) to the same date.
 *
 * The bracketed placeholders — [RAZÃO SOCIAL], [CNPJ], [ENDEREÇO], [CIDADE/UF], [NOME DO
 * ENCARREGADO] — are for the owner to fill in before the text is relied on.
 */

import { useQuery } from '@tanstack/react-query'
import type { Locale } from './i18n'

/** Must equal TermsOfUse.CurrentVersion on the server. Shown on the page, never compared here. */
export const TERMS_VERSION = '2026-10-03'

/** Where the text is served from: next to index.html, on the web and in the desktop app alike. */
export const TERMS_URL = '/legal/terms.json'

export interface TermsSection {
  /** An anchor for a link from outside, such as "excluir-conta" in the Privacy Policy. */
  id?: string
  heading: string
  paragraphs?: string[]
  items?: string[]
}

export interface TermsDocument {
  title: string
  versionLabel: string
  intro: string[]
  sections: TermsSection[]
}

export type TermsDocuments = Record<Locale, TermsDocument>

async function fetchTerms(): Promise<TermsDocuments> {
  const response = await fetch(TERMS_URL, { cache: 'no-cache' })
  if (!response.ok) throw new Error(`terms: ${response.status}`)
  return (await response.json()) as TermsDocuments
}

/** The terms in the reader's language: Portuguese for pt-BR, English otherwise. Undefined while loading. */
export function useTermsDocument(locale: Locale) {
  const query = useQuery({ queryKey: ['terms', TERMS_VERSION], queryFn: fetchTerms, staleTime: Infinity, retry: 2 })
  return { document: query.data?.[locale === 'pt-BR' ? 'pt-BR' : 'en-US'], failed: query.isError }
}
